// deps
import React, { ReactElement, memo, useCallback, useMemo } from "react";

// internal-deps
import { BottomSheet, format, getCloudinaryUrl } from "@swiggy-private/core-ui";
import { User } from "@swiggy-private/payment-core";
import { getPayLaterTestIds, PayLaterTestIds } from "@swiggy-private/payment-test-ids";

// constants
import { TEXTS as LinkAccountTexts } from "@payment-ui/constants/LinkAccount";
import { TEXTS as PaylaterTexts } from "@payment-ui/constants/PayLater";

// components
import { LinkAccountContent } from "@payment-ui/components/link-account-content";
import { MetaPoints } from "./MetaPoints";
import { TnC } from "./Tnc";

// widgets
import { PaymentMethodIcon } from "@payment-ui/widgets/payment-method-icon";

// interfaces
import { PayLaterLinkedData } from "@payment-ui/interfaces";

// helpers
import { getPayLaterLinkedMetaText } from "@payment-ui/helpers/PayLater";

//styles
import styles from "../PayLater.v4.scss";

interface ILinkHalfCardProps {
    user: User;
    methodMeta: {
        displayName: string;
        paymentCode: string;
        iconUrl?: string;
        tncUrl?: string;
    };
    onProceedToLink: (payLaterCode: string) => void;
    onCloseHalfCard?: VoidFunction;
    payLaterLinkedData: PayLaterLinkedData;
    showAsFullPage: boolean;
}

interface DialogHeaderParams {
    renderIcon: (width?: number) => ReactElement;
    payLaterTestIds: PayLaterTestIds;
    payLaterLinkedData?: PayLaterLinkedData;
}

interface DialogContentParams {
    methodMeta: {
        displayName: string;
        paymentCode: string;
        iconUrl?: string;
        tncUrl?: string;
    };
    user: User;
    payLaterTestIds: PayLaterTestIds;
    payLaterLinkedData?: PayLaterLinkedData;
}

const _renderDialogHeader = ({ renderIcon, payLaterTestIds, payLaterLinkedData }: DialogHeaderParams): ReactElement => {
    return (
        <div className={styles.dialogHeader}>
            <div className={styles.dialogHeaderIcon} data-testid={payLaterTestIds.DIALOG_HEADER.ICON}>
                {renderIcon()}
            </div>
            {payLaterLinkedData && (
                <div className={styles.dialogHeaderText} data-testid={payLaterTestIds.DIALOG_HEADER.TEXT}>
                    {getPayLaterLinkedMetaText(payLaterLinkedData)}
                </div>
            )}
        </div>
    );
};

const _renderDialogContent = ({
    methodMeta,
    user,
    payLaterLinkedData,
    payLaterTestIds,
}: DialogContentParams): ReactElement => {
    const { displayName, tncUrl } = methodMeta;
    return (
        <div className={styles.dialogContent} data-testid={payLaterTestIds.DIALOG_CONTENT.WRAPPER}>
            {payLaterLinkedData && (
                <MetaPoints meta={payLaterLinkedData} dataTestId={payLaterTestIds.DIALOG_CONTENT.META_POINT} />
            )}
            <TnC tncUrl={tncUrl} dataTestId={payLaterTestIds.DIALOG_CONTENT.TNC_LINK} />
            <div className={styles.dialogContentText} data-testid={payLaterTestIds.DIALOG_CONTENT.METHOD_NAME}>
                {format(PaylaterTexts.LINK_TEXT_V4, displayName)}
            </div>
            <div className={styles.dialogContentSubtext} data-testid={payLaterTestIds.DIALOG_CONTENT.EMAIL}>
                {user.email}
            </div>
            <div
                className={styles.dialogContentNumber}
                data-testid={payLaterTestIds.DIALOG_CONTENT.MOBILE}
            >{`${user.mobile}`}</div>
        </div>
    );
};

function LinkHalfCard(props: ILinkHalfCardProps): ReactElement {
    const { user, onCloseHalfCard, onProceedToLink, methodMeta, payLaterLinkedData, showAsFullPage } = props;
    const { paymentCode, iconUrl } = methodMeta;
    const payLaterTestIds = useMemo(() => getPayLaterTestIds(paymentCode), [paymentCode]);

    const onConfirm = useCallback((): void => {
        if (typeof onCloseHalfCard === "function") {
            onCloseHalfCard();
        }
        onProceedToLink(paymentCode);
    }, [onCloseHalfCard, onProceedToLink, paymentCode]);

    const onClose = useCallback((): void => {
        if (typeof onCloseHalfCard === "function") {
            onCloseHalfCard();
        }
    }, [onCloseHalfCard]);

    const renderIcon = useCallback(
        (width?: number): ReactElement => (
            <PaymentMethodIcon
                src={getCloudinaryUrl(iconUrl)}
                width={width}
                data-testid={payLaterTestIds.ICON}
                imgClassName={styles.payLaterLinkHeaderIconImg}
                parentClassName={styles.payLaterLinkHeaderIcon}
            />
        ),
        [iconUrl, payLaterTestIds.ICON],
    );

    const renderDialogHeader = useCallback(
        (): ReactElement => _renderDialogHeader({ renderIcon: renderIcon, payLaterLinkedData, payLaterTestIds }),
        [payLaterLinkedData, payLaterTestIds, renderIcon],
    );

    const renderDialogContent = useCallback(() => {
        if (typeof _renderDialogContent === "function" && user) {
            return _renderDialogContent({ methodMeta, user, payLaterLinkedData, payLaterTestIds });
        }
    }, [methodMeta, payLaterLinkedData, payLaterTestIds, user]);

    const renderHalfcardContent = useCallback(() => {
        return (
            <BottomSheet hideOnOverlayClick={true} onClose={onClose}>
                {({ close }): React.ReactElement => (
                    <LinkAccountContent
                        renderHeader={renderDialogHeader}
                        renderContent={renderDialogContent}
                        primaryAction={{
                            text: LinkAccountTexts.CONFIRM_AND_PROCEED,
                            onClick: onConfirm,
                        }}
                        secondaryAction={{
                            text: LinkAccountTexts.LINK_DIALOG_SECONDARYRY_ACTION_TEXT,
                            onClick: close,
                        }}
                    />
                )}
            </BottomSheet>
        );
    }, [onClose, onConfirm, renderDialogContent, renderDialogHeader]);

    return (
        <>
            {showAsFullPage ? (
                <LinkAccountContent
                    renderHeader={renderDialogHeader}
                    renderContent={renderDialogContent}
                    primaryAction={{
                        text: LinkAccountTexts.CONFIRM_AND_PROCEED,
                        onClick: onConfirm,
                    }}
                />
            ) : (
                renderHalfcardContent()
            )}
        </>
    );
}

export default memo(LinkHalfCard);
